import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { BrowseFields, readBrowseFields } from '../components/BrowseFields';
import { PartnerOrders } from '../components/PartnerOrders';
import { PartnerProtection } from '../components/PartnerProtection';
import { ProductScanner } from '../components/ProductScanner';
import { BulkAdd } from '../components/partner/BulkAdd';
import { PlanPackages } from '../components/partner/PlanPackages';
import { ProductCard } from '../components/partner/ProductCard';
import { ProductForm } from '../components/partner/ProductForm';
import { ReferralCard } from '../components/ReferralCard';
import { ShowWordsSwitch } from '../components/ShowWordsSwitch';
import { TermsChanged } from '../components/TermsChanged';
import { storeConfig } from '../config';
import {
  addPartnerProduct,
  agreePartnerTerms,
  answerPartnerEnquiry,
  confirmPartnerCheckout,
  downloadBusinessStatement,
  fetchBusinessMe,
  fetchPartnerDashboard,
  orderPartnerProducts,
  savePartnerProfile,
  PARTNER_RECEIPT_KEY,
  rememberBusinessToken,
  removePartnerProduct,
  setPartnerPrice,
  startPartnerCheckout,
  takeFreeSpotlightWeek,
  type PartnerDashboard as Dashboard,
  type PartnerProductRow,
} from '../lib/api';
import { click } from '../lib/alert';
import { confirmedSignedOut, useGentleRefresh } from '../lib/session-guard';
import { money } from '../lib/money';
import { useOzi } from '../state/ozi';
import { InboxLink } from '../components/Inbox';
import { readMessagesAloud } from '../lib/inbox-api';
import { partnerHelp, understandPartner } from '../voice/business-voice';
import { LiveClock } from '../components/LiveClock';
import { Reminders } from '../components/Reminders';
import { everydayOr } from '../voice/phrases';
import { listWords, spokenMoney } from '../voice/staff-voice';
import { spokenDate } from '../components/StaffVoice';

const field = 'w-full min-h-control rounded-2xl border-2 border-paper bg-paper text-ink p-3';
const YES = /\b(yes|yeah|yep|correct|that'?s right|go ahead|confirm|do it|ok|okay)\b/i;
const STATUS_WORDS: Record<PartnerProductRow['status'], string> = {
  pending: 'waiting to be checked',
  approved: 'live',
  rejected: 'not accepted',
  removed: 'taken off',
};

function longDate(when: string): string {
  return new Date(when).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/** The receipt for a Checkout page, kept until Stripe sends the shop back (ruling 65). */
const RECEIPT = PARTNER_RECEIPT_KEY;

/**
 * A Shop Partner's own area (7 October 2026; ruling 65): what they pay and paying it here, their
 * products or services and prices, adding one with a photo (checked by us before Shoppers see it),
 * a restaurant's agreed discount and its Preferred Partner standing (no commission, Anthony, 30
 * October 2026), a service's enquiries, and a link to share. Ozi helps here too, with this
 * dashboard's own commands only.
 */
export function PartnerDashboard(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const ozi = useOzi();
  const [data, setData] = useState<Dashboard | null>(null);
  const [problem, setProblem] = useState('');
  const [news, setNews] = useState((location.state as { welcome?: string } | null)?.welcome ?? '');
  const [busy, setBusy] = useState(false);
  // Adding a size or pack of this product (ruling 96), on the form below the list.
  const [variantOf, setVariantOf] = useState<PartnerProductRow | null>(null);
  const latest = useRef({ ozi, data, navigate });
  const phraseTurn = useRef(0);
  latest.current = { ozi, data, navigate };
  // While products are being scanned, what Ozi hears about the list is handled there first.
  const scanVoice = useRef<((text: string) => boolean) | null>(null);
  const registerScanVoice = useCallback((handler: ((text: string) => boolean) | null) => {
    scanVoice.current = handler;
  }, []);
  // Likewise the orders section's own commands (ruling 97): "read my orders", "order ready".
  const ordersVoice = useRef<((text: string) => boolean) | null>(null);
  const registerOrdersVoice = useCallback((handler: ((text: string) => boolean) | null) => {
    ordersVoice.current = handler;
  }, []);
  const sayIt = useCallback((words: string) => void latest.current.ozi.say(words), []);
  const confirmIt = useCallback((question: string, then: (yes: boolean) => void) => {
    latest.current.ozi.listenFor(question, (answer) => then(YES.test(answer)));
  }, []);

  const load = useCallback(
    () =>
      fetchPartnerDashboard()
        .then((result) => {
          setData(result);
          return result;
        })
        .catch(async (failure: unknown) => {
          // Only the server's own word that the sign-in itself has ended, said twice ten seconds
          // apart, sends anyone back to sign in (ruling 88). Anything else is said plainly.
          if (await confirmedSignedOut(failure, fetchBusinessMe)) {
            rememberBusinessToken(null);
            navigate('/business');
          } else {
            setProblem(failure instanceof Error ? failure.message : 'Something went wrong.');
          }
          return null;
        }),
    [navigate],
  );

  // Coming back to the screen, and once an hour while open, asks "me" again, so a session in use
  // is renewed before it ends (ruling 88). Never signs anybody out.
  const keepFresh = useCallback(async () => {
    await fetchBusinessMe().catch(() => undefined);
  }, []);
  useGentleRefresh(keepFresh);

  const shareUrl = data ? `${window.location.origin}${data.sharePath}` : '';

  /** Paying for Partner Plus or Spotlight: the free first month at once, or off to Stripe's page. */
  const pay = useCallback(
    async (kind: 'plan' | 'pro' | 'spotlight' | 'plus'): Promise<void> => {
      setBusy(true);
      setProblem('');
      try {
        const result = await startPartnerCheckout({ kind, months: 1, returnTo: '/partner' });
        if (result.url && result.receipt) {
          try {
            window.sessionStorage.setItem(RECEIPT, result.receipt);
          } catch {
            // Private browsing: the payment is still recorded by hand if the receipt is lost.
          }
          setNews(result.message);
          window.location.assign(result.url);
          return;
        }
        setNews(result.message);
        void latest.current.ozi.say(result.message);
        await load();
      } catch (failure) {
        setProblem(failure instanceof Error ? failure.message : 'That did not work.');
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  // Back from Stripe with ?paid=<session>: record it, once, with the receipt kept before leaving.
  useEffect(() => {
    const sessionId = params.get('paid');
    if (!sessionId) return;
    let receipt = '';
    try {
      receipt = window.sessionStorage.getItem(RECEIPT) ?? '';
      window.sessionStorage.removeItem(RECEIPT);
    } catch {
      receipt = '';
    }
    setParams({}, { replace: true });
    if (!receipt) {
      setProblem('Thank you for paying. If your plan does not show as paid shortly, ring us.');
      return;
    }
    confirmPartnerCheckout(receipt, sessionId)
      .then((result) => {
        setNews(result.message);
        void latest.current.ozi.say(result.message);
        void load();
      })
      .catch((failure: unknown) =>
        setProblem(failure instanceof Error ? failure.message : 'That did not work.'),
      );
  }, [params, setParams, load]);

  const share = useCallback(async (): Promise<string> => {
    const current = latest.current.data;
    if (!current) return '';
    const url = `${window.location.origin}${current.sharePath}`;
    const text = `${current.shop.name} on ${storeConfig.productName}: order from us and have it brought to your door.`;
    try {
      if (navigator.share) {
        await navigator.share({ title: current.shop.name, text, url });
        return 'Shared.';
      }
      await navigator.clipboard?.writeText(url);
      return 'Your link is copied. Paste it into a message, WhatsApp or an email.';
    } catch {
      return `Your link is ${url}`;
    }
  }, []);

  useEffect(() => {
    const say = (words: string): void => void latest.current.ozi.say(words);
    const ask = (question: string, then: (answer: string) => void): void => {
      latest.current.ozi.listenFor(question, then);
    };
    const find = (name: string): PartnerProductRow | undefined =>
      latest.current.data?.products.find((product) =>
        product.name.toLowerCase().includes(name.toLowerCase().replace(/^the /, '')),
      );

    const handle = (text: string): boolean => {
      if (scanVoice.current?.(text)) return true;
      if (ordersVoice.current?.(text)) return true;
      const command = understandPartner(text);
      const current = latest.current.data;
      if (!command) {
        everydayOr(
          text,
          phraseTurn,
          { kind: 'business' },
          say,
          `Sorry, I didn't catch that. ${partnerHelp()}`,
          (work) => latest.current.ozi.think(work),
          ask,
        );
        return true;
      }
      switch (command.kind) {
        case 'help':
          say(partnerHelp());
          return true;
        case 'messages':
          void readMessagesAloud('business', latest.current.ozi, storeConfig.productName);
          return true;
        case 'sign-out':
          rememberBusinessToken(null);
          say('Signed out. Goodbye.');
          latest.current.navigate('/business');
          return true;
        case 'plan':
          if (current) {
            say(
              `${current.plan.words}${current.shop.kind === 'shop' ? ' To pay or renew, use the button under Your plan on the screen, or ring us.' : ''}`,
            );
          }
          return true;
        case 'share':
          void share().then(say);
          return true;
        case 'payments':
          if (current) {
            say(
              current.payments.length === 0
                ? 'No payments are recorded yet.'
                : `${listWords(current.payments.slice(0, 5).map((row) => `${longDate(row.paidAt)}, ${row.what}, ${money(row.amountPence)}`))}. To download your statement, use the button on the screen.`,
            );
          }
          return true;
        case 'numbers':
          if (current) {
            say(
              `This week, ${current.numbers.purchasesThisWeek} purchase${current.numbers.purchasesThisWeek === 1 ? '' : 's'} from your shop. This month, ${current.numbers.purchasesThisMonth}, with ${current.numbers.itemsThisMonth} items. ${current.referrals} ${current.referrals === 1 ? 'person has' : 'people have'} joined through your link.`,
            );
          }
          return true;
        case 'spotlight':
          if (current) {
            const level = current.spotlight.level;
            say(
              level === 'none'
                ? `You are not on Spotlight. With Spotlight, at ${money(current.spotlight.prices.spotlightPence)} a month, I mention your shop to Shoppers looking for what you sell, up to once a week each; Spotlight Plus, at ${money(current.spotlight.prices.plusPence)}, up to ${current.spotlight.prices.plusPerWeek} times. ${current.shop.kind === 'shop' && current.plan.tier === 'plus' && current.plan.paid ? 'Use the buttons under Spotlight on the screen, or speak to us.' : 'Spotlight is for shops on paid Partner Plus.'}`
                : `You are on ${level === 'plus' ? 'Spotlight Plus' : 'Spotlight'}${current.spotlight.until ? ` until ${longDate(current.spotlight.until)}` : ''}. I have mentioned your shop ${current.spotlight.mentionsThisMonth} time${current.spotlight.mentionsThisMonth === 1 ? '' : 's'} this month.`,
            );
          }
          return true;
        case 'products': {
          if (!current) return true;
          const { live, waiting, notAccepted } = current.counts;
          const names = current.products.map(
            (product) =>
              `${product.name}, ${money(product.pricePence)}, ${STATUS_WORDS[product.status]}`,
          );
          say(
            `You have ${live} live, ${waiting} waiting to be checked${notAccepted ? ` and ${notAccepted} not accepted` : ''}. ` +
              (names.length ? `${listWords(names)}.` : 'Say "add a product" to add your first.'),
          );
          return true;
        }
        case 'remove': {
          const product = find(command.name);
          if (!product) {
            say(`I couldn't find ${command.name} in your products.`);
            return true;
          }
          ask(
            `Take ${product.name} off, so Shoppers no longer see it? Say yes to confirm.`,
            (answer) => {
              if (!YES.test(answer)) return say('All right, it stays.');
              void removePartnerProduct(product.id).then((result) => {
                say(result.message);
                void load();
              });
            },
          );
          return true;
        }
        case 'price': {
          const product = find(command.name);
          if (!product || command.pricePence === null) {
            say(
              product
                ? "I didn't catch the price."
                : `I couldn't find ${command.name} in your products.`,
            );
            return true;
          }
          const pence = command.pricePence;
          ask(`Change ${product.name} to ${money(pence)}? Say yes to confirm.`, (answer) => {
            if (!YES.test(answer)) return say('All right, the price stays.');
            void setPartnerPrice(product.id, pence).then((result) => {
              say(result.message);
              void load();
            });
          });
          return true;
        }
        case 'add': {
          if (current?.shop.kind === 'service') {
            // A service: a name, a price from, and how long it takes (ruling 65).
            ask(
              'What is the service? Say its name as Shoppers should see it, such as haircut.',
              (name) => {
                ask(`And the price of ${name}, from?`, (priceWords) => {
                  const pence = spokenMoney(priceWords);
                  if (pence === null) return say("I didn't catch a price, so nothing was added.");
                  ask('How long does it take? Say the minutes, such as thirty.', (minutesWords) => {
                    const minutes = Number(
                      /\d+/.exec(minutesWords)?.[0] ?? (spokenMoney(minutesWords) ?? 0) / 100,
                    );
                    if (!Number.isFinite(minutes) || minutes < 5) {
                      return say("I didn't catch how long it takes, so nothing was added.");
                    }
                    ask(
                      `Send ${name}, from ${money(pence)}, about ${minutes} minutes, to be checked? Say yes to confirm.`,
                      (answer) => {
                        if (!YES.test(answer)) return say('All right, nothing was added.');
                        void addPartnerProduct({
                          name: name.trim(),
                          pricePence: pence,
                          tags: '',
                          durationMinutes: minutes,
                        })
                          .then((result) => {
                            click();
                            say(result.message);
                            void load();
                          })
                          .catch((failure: unknown) =>
                            say(failure instanceof Error ? failure.message : 'That did not work.'),
                          );
                      },
                    );
                  });
                });
              },
            );
            return true;
          }
          ask(
            'What is the product? Say its name as Shoppers should see it, such as sourdough loaf, 800 grams.',
            (name) => {
              ask(`And the price of ${name}?`, (priceWords) => {
                const pence = spokenMoney(priceWords);
                if (pence === null) return say("I didn't catch a price, so nothing was added.");
                ask(
                  'Does it have a best before or use by date? Say the date, or say no.',
                  (dateWords) => {
                    const expiresOn = /\b(no|none|nope)\b/i.test(dateWords)
                      ? undefined
                      : (spokenDate(dateWords) ?? undefined);
                    ask(
                      `Send ${name}, ${money(pence)}${expiresOn ? `, best before ${longDate(expiresOn)}` : ''}, to be checked? You can add a photo on the screen afterwards by adding it again with a photo. Say yes to confirm.`,
                      (answer) => {
                        if (!YES.test(answer)) return say('All right, nothing was added.');
                        void addPartnerProduct({
                          name: name.trim(),
                          pricePence: pence,
                          tags: '',
                          ...(expiresOn ? { expiresOn } : {}),
                        })
                          .then((result) => {
                            click();
                            say(result.message);
                            void load();
                          })
                          .catch((failure: unknown) =>
                            say(failure instanceof Error ? failure.message : 'That did not work.'),
                          );
                      },
                    );
                  },
                );
              });
            },
          );
          return true;
        }
      }
    };

    latest.current.ozi.setPageCommands(handle);
    void load().then((result) => {
      if (!result) return;
      const { live, waiting } = result.counts;
      const things = result.shop.kind === 'service' ? 'service' : 'product';
      say(
        `Welcome to your ${storeConfig.productName} Shop Partner area for ${result.shop.name}. ` +
          `You have ${live} ${things}${live === 1 ? '' : 's'} live and ${waiting} waiting to be checked. ` +
          (result.shop.status === 'pending'
            ? 'Your page goes public once we have checked your listing. '
            : '') +
          (result.shop.kind === 'shop' && result.plan.tier === 'plus' && !result.plan.paid
            ? 'Partner Plus is not paid at the moment. '
            : '') +
          `Say "add a ${things}", or "help".`,
      );
    });
    return () => latest.current.ozi.setPageCommands(null);
  }, [load, share]);

  /** A product added or changed on a form (ruling 96): said, shown, and the list refreshed. */
  const added = (message: string): void => {
    click();
    setProblem('');
    setNews(message);
    void ozi.say(message);
    setVariantOf(null);
    void load();
  };

  /** A Pro shop moves a product up or down its page: the whole order is sent (ruling 96). */
  const move = (productId: string, direction: -1 | 1): void => {
    const ids = (data?.products ?? [])
      .filter((product) => product.status !== 'rejected')
      .map((product) => product.id);
    const at = ids.indexOf(productId);
    const to = at + direction;
    if (at === -1 || to < 0 || to >= ids.length) return;
    ids.splice(at, 1);
    ids.splice(to, 0, productId);
    void orderPartnerProducts(ids)
      .then((result) => {
        setNews(result.message);
        void load();
      })
      .catch((failure: unknown) =>
        setProblem(failure instanceof Error ? failure.message : 'That did not work.'),
      );
  };

  if (!data) {
    return (
      <p role="status" className="m-0">
        One moment.
      </p>
    );
  }

  const service = data.shop.kind === 'service';
  // Spotlight is bought here by a shop on paid Partner Plus, through the plan's own Checkout.
  // An Ozi Preferred Partner has Partner Plus free (30 October 2026), so it may buy Spotlight too.
  const spotlightOpen =
    (data.shop.kind === 'shop' && data.plan.tier === 'plus' && data.plan.paid) ||
    data.restaurant?.preferred === true;
  return (
    <div className="space-y-8 max-w-2xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-display font-bold m-0">{data.shop.name}</h1>
        <LiveClock />
      </div>
      <nav aria-label="Your messages">
        <InboxLink as="business" to="/partner/messages" />
      </nav>
      {data.terms?.changed && (
        <TermsChanged
          words={data.terms.words ?? ''}
          termsName="Shop Partner terms"
          termsPath={data.terms.path}
          onAgree={async () => {
            const result = await agreePartnerTerms(data.terms?.version ?? '');
            setNews(result.message);
            await load();
          }}
        />
      )}
      <p className="m-0">
        <Link to="/partner/pay-by-bank" className="control border-paper bg-paper/10 text-paper">
          Pay us by bank: our details and your reference
        </Link>
      </p>
      {/* Reminders for everyone (ruling 93), on this dashboard too. */}
      <Reminders as="business" />
      <details className="max-w-xl">
        <summary className="control px-0 text-paper underline">The screen</summary>
        <ShowWordsSwitch />
      </details>
      <p className="m-0">
        Your Shop Partner area. {data.shop.kindWords}
        {data.shop.postcode ? `, ${data.shop.postcode}` : ''}.
      </p>
      {data.shop.status === 'pending' && (
        <p role="status" className="m-0 border-2 border-paper rounded-2xl p-4">
          Your page is not public yet: we check every new listing, usually within one working day.
          You can add your {service ? 'services' : 'products'} now, and they go live with it.
        </p>
      )}
      {!data.shop.active && data.shop.status !== 'pending' && (
        <p role="status" className="m-0 border-2 border-paper rounded-2xl p-4">
          Your listing is paused. Please ring us.
        </p>
      )}
      <p role="status" className="m-0 min-h-control">
        {news}
      </p>
      {problem !== '' && (
        <p role="alert" className="border-2 border-paper bg-paper text-ink p-4 rounded-2xl m-0">
          {problem}
        </p>
      )}

      {/* The payment warning, kept on the dashboard, and the business's Ambassador (31 October 2026). */}
      <PartnerProtection />

      {/* The shop's orders and its Runner (ruling 97). */}
      <PartnerOrders registerVoice={registerOrdersVoice} />

      <section
        aria-labelledby="plan-heading"
        className="space-y-2 border-2 border-highlight rounded-2xl p-4"
      >
        <h2 id="plan-heading" className="text-lead font-bold m-0">
          Your plan
        </h2>
        <p className="m-0">{data.plan.words}</p>
        <p className="m-0">
          {data.counts.live} live, {data.counts.waiting} waiting to be checked
          {data.counts.notAccepted ? `, ${data.counts.notAccepted} not accepted` : ''}
          {data.plan.productLimit !== null
            ? `; ${data.plan.productsListed} of ${data.plan.productLimit} places used`
            : ''}
          .
        </p>
        {/* The three packages (ruling 96): what each gives, and one button each. */}
        {data.plan.packages && (data.shop.kind === 'shop' || data.restaurant?.preferred) && (
          <>
            <PlanPackages packages={data.plan.packages} busy={busy} onPay={(kind) => void pay(kind)} />
            <p className="m-0">
              Or ring us on {storeConfig.contact.telephonePlaceholder} and a person will take it.
              Nothing is ever taken without you.
            </p>
          </>
        )}
        {!data.plan.packages && data.shop.kind === 'shop' && (
          <div className="flex flex-wrap gap-2 items-center">
            <button
              type="button"
              disabled={busy}
              onClick={() => void pay('plan')}
              className="control bg-highlight text-on-accent disabled:opacity-70"
            >
              {data.plan.tier !== 'free' && data.plan.paid
                ? `Renew, ${money(data.plan.plusMonthlyPence)} for a month`
                : data.plan.firstMonthFree
                  ? 'Start, first month free'
                  : `Pay, ${money(data.plan.plusMonthlyPence)} a month`}
            </button>
            <span>
              Or ring us on {storeConfig.contact.telephonePlaceholder} and a person will take it.
            </span>
          </div>
        )}
      </section>

      {data.restaurant && (
        <section
          aria-labelledby="restaurant-heading"
          className="space-y-2 border-2 border-paper rounded-2xl p-4"
        >
          <h2 id="restaurant-heading" className="text-lead font-bold m-0">
            {data.restaurant.preferred
              ? `${storeConfig.assistantName} Preferred Partner`
              : `Your price for ${storeConfig.assistantName}`}
          </h2>
          <p className="m-0">
            No commission: you owe {storeConfig.productName} nothing on your orders.{' '}
            {data.restaurant.words}
          </p>
          <p className="m-0">
            This month: {data.restaurant.ordersThisMonth} order
            {data.restaurant.ordersThisMonth === 1 ? '' : 's'} through {storeConfig.productName},{' '}
            {money(data.restaurant.goodsThisMonthPence)} of food and drink at your shop prices.
          </p>
          <p className="m-0">
            To agree a discount, or change it, ring us on {storeConfig.contact.telephonePlaceholder}
            .
          </p>
        </section>
      )}

      {service && (
        <section aria-labelledby="enquiries-heading" className="space-y-3">
          <h2 id="enquiries-heading" className="text-lead font-bold m-0">
            Enquiries
          </h2>
          <p className="m-0">
            Shoppers asking you for a quote or a call back. Get back to them yourself; nothing is
            paid through {storeConfig.productName} for services yet.
          </p>
          {data.enquiries.length === 0 && <p className="m-0">None yet.</p>}
          <ul className="list-none m-0 p-0 space-y-3">
            {data.enquiries.map((row) => (
              <li key={row.id} className="border-2 border-paper/40 rounded-2xl p-4 space-y-2">
                <h3 className="m-0 font-bold">
                  {row.shopperName} asked for {row.what}
                </h3>
                <p className="m-0">
                  {longDate(row.createdAt)}. {row.message || 'No message.'} Reach them on:{' '}
                  {row.contact}.{row.status === 'answered' ? ' Answered.' : ''}
                </p>
                {row.status === 'open' && (
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      const note = String(
                        new FormData(event.currentTarget).get('reply') ?? '',
                      ).trim();
                      void answerPartnerEnquiry(row.id, note).then((result) => {
                        setNews(result.message);
                        void load();
                      });
                    }}
                    className="flex flex-wrap gap-2 items-end"
                  >
                    <label className="block">
                      A note for them (optional)
                      <input name="reply" className={field} />
                    </label>
                    <button type="submit" className="control border-paper bg-paper/10 text-paper">
                      Mark as answered
                      <span className="visually-hidden"> for {row.shopperName}</span>
                    </button>
                  </form>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section
        aria-labelledby="found-heading"
        className="space-y-3 border-2 border-paper/40 rounded-2xl p-4"
      >
        <h2 id="found-heading" className="text-lead font-bold m-0">
          How Shoppers find you
        </h2>
        <p className="m-0">
          {service
            ? 'Shoppers browse services by kind on the Services page.'
            : data.shop.kind === 'food'
              ? 'Shoppers browse restaurants, cafés and takeaways by cuisine on the Restaurants page.'
              : 'Shoppers browse shops by speciality on the Shops page, and corner shops on the Convenience stores page.'}
        </p>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const form = event.currentTarget;
            setProblem('');
            void savePartnerProfile({
              ...readBrowseFields(form, 'profile', data.shop.kind),
              about: String(new FormData(form).get('profile-about') ?? '').trim(),
            })
              .then((result) => {
                setNews(result.message);
                void load();
              })
              .catch((failure: unknown) =>
                setProblem(failure instanceof Error ? failure.message : 'That could not be saved.'),
              );
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <label htmlFor="profile-about" className="block text-lead font-bold">
              {service ? 'What you do' : 'What you sell'}, in a sentence
            </label>
            <textarea
              id="profile-about"
              name="profile-about"
              rows={3}
              defaultValue={data.shop.about}
              className={field}
            />
          </div>
          <BrowseFields
            key={JSON.stringify([
              data.shop.cuisines,
              data.shop.specialities,
              data.shop.categories,
              data.shop.convenience,
              data.shop.ofstedNumber,
            ])}
            kind={data.shop.kind}
            prefix="profile"
            initial={data.shop}
          />
          <button type="submit" className="control bg-highlight text-on-accent">
            Save how Shoppers find you
          </button>
        </form>
      </section>

      <section aria-labelledby="numbers-heading" className="space-y-2">
        <h2 id="numbers-heading" className="text-lead font-bold m-0">
          How your shop is doing
        </h2>
        <p className="m-0">
          This week, {data.numbers.purchasesThisWeek} purchase
          {data.numbers.purchasesThisWeek === 1 ? '' : 's'} from your shop. This month,{' '}
          {data.numbers.purchasesThisMonth}, with {data.numbers.itemsThisMonth} items.
        </p>
      </section>

      <section
        aria-labelledby="spotlight-heading"
        className="space-y-2 border-2 border-paper rounded-2xl p-4"
      >
        <h2 id="spotlight-heading" className="text-lead font-bold m-0">
          Spotlight: {storeConfig.assistantName} mentions your shop
        </h2>
        {data.spotlight.level === 'none' ? (
          <p className="m-0">
            Not on Spotlight. Spotlight, {money(data.spotlight.prices.spotlightPence)} a month:{' '}
            {storeConfig.assistantName} mentions your shop to Shoppers looking for what you sell, up
            to{' '}
            {data.spotlight.prices.spotlightPerWeek === 1
              ? 'once'
              : `${data.spotlight.prices.spotlightPerWeek} times`}{' '}
            a week each. Spotlight Plus, {money(data.spotlight.prices.plusPence)} a month: up to{' '}
            {data.spotlight.prices.plusPerWeek} times a week. On top of your plan
            {spotlightOpen
              ? '; pay for it here, or speak to us.'
              : ', for shops on paid Partner Plus.'}
          </p>
        ) : (
          <p className="m-0">
            On {data.spotlight.level === 'plus' ? 'Spotlight Plus' : 'Spotlight'}
            {data.spotlight.until ? ` until ${longDate(data.spotlight.until)}` : ''}. Mentioned{' '}
            {data.spotlight.mentionsThisMonth} time
            {data.spotlight.mentionsThisMonth === 1 ? '' : 's'} this month. Each mention is called
            an advert, after {storeConfig.assistantName}'s own answer.
          </p>
        )}
        {data.spotlight.freeWeekLeft && (
          <div className="space-y-2">
            <p className="m-0">
              As a {storeConfig.assistantName} Preferred Partner you have a free Spotlight week this
              month. Nothing is charged for it.
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setProblem('');
                void takeFreeSpotlightWeek()
                  .then((result) => {
                    setNews(result.message);
                    void load();
                  })
                  .catch((failure: unknown) =>
                    setProblem(
                      failure instanceof Error ? failure.message : 'That could not be done.',
                    ),
                  );
              }}
              className="control bg-highlight text-on-accent disabled:opacity-70"
            >
              Start my free Spotlight week
            </button>
          </div>
        )}
        {spotlightOpen && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void pay('spotlight')}
              className="control border-paper bg-paper/10 text-paper disabled:opacity-70"
            >
              {data.spotlight.level === 'spotlight' ? 'Renew Spotlight' : 'Buy Spotlight'},{' '}
              {money(data.spotlight.prices.spotlightPence)} for a month
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void pay('plus')}
              className="control border-paper bg-paper/10 text-paper disabled:opacity-70"
            >
              {data.spotlight.level === 'plus' ? 'Renew Spotlight Plus' : 'Buy Spotlight Plus'},{' '}
              {money(data.spotlight.prices.plusPence)} for a month
            </button>
          </div>
        )}
      </section>

      <section aria-labelledby="payments-heading" className="space-y-2">
        <h2 id="payments-heading" className="text-lead font-bold m-0">
          What you have paid
        </h2>
        {data.payments.length === 0 ? (
          <p className="m-0">No payments recorded yet.</p>
        ) : (
          <ul className="m-0 ps-6 space-y-1">
            {data.payments.map((row) => (
              <li key={row.id}>
                {longDate(row.paidAt)}: {row.what}, {row.months} month{row.months === 1 ? '' : 's'},{' '}
                {money(row.amountPence)}. Covers until {longDate(row.coversUntil)}.
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          onClick={() =>
            void downloadBusinessStatement('partner', data.shop.name)
              .then(() => setNews('Your statement is downloading, as a PDF.'))
              .catch((failure: unknown) =>
                setProblem(failure instanceof Error ? failure.message : 'That did not work.'),
              )
          }
          className="control border-paper bg-paper/10 text-paper"
        >
          Download my statement (PDF)
        </button>
      </section>

      <section aria-labelledby="share-heading" className="space-y-2">
        <h2 id="share-heading" className="text-lead font-bold m-0">
          Share your shop
        </h2>
        <p className="m-0">
          Send your page to your customers, other shops and friends. They order from you, and a
          Runner brings it to their door.
        </p>
        <p className="m-0 break-all">{shareUrl}</p>
        {/* Only the count: there is no target to reach (30 October 2026). */}
        <p className="m-0 font-bold">
          {data.referrals} {data.referrals === 1 ? 'person has' : 'people have'} joined through your
          link
        </p>
        <button
          type="button"
          onClick={() => void share().then((words) => setNews(words))}
          className="control bg-highlight text-on-accent"
        >
          Share my shop’s link
        </button>{' '}
        <Link to={data.sharePath} className="control bg-paper/10 text-paper underline">
          See my page as Shoppers do
        </Link>
      </section>

      {/* Their own referral link, and Ozi Ambassadors (Anthony, 30 October 2026). */}
      <ReferralCard as="business" onNews={setNews} />

      {!service && (
        <ProductScanner
          plus={(data.plan.limits?.photosPerProduct ?? (data.plan.productLimit === null ? 1 : 0)) > 0}
          placesLeft={
            data.plan.productLimit === null
              ? null
              : Math.max(0, data.plan.productLimit - data.plan.productsListed)
          }
          products={data.products}
          say={sayIt}
          confirm={confirmIt}
          onAdded={() => void load()}
          registerVoice={registerScanVoice}
        />
      )}

      {/* The add form (ruling 96): every detail the catalogue keeps, photos taken or chosen. */}
      {data.catalogue && data.plan.limits && (
        <div id="add-product">
          <ProductForm
            key={variantOf?.id ?? 'new'}
            kind={data.shop.kind}
            catalogue={data.catalogue}
            limits={data.plan.limits}
            {...(variantOf ? { variantOf } : {})}
            busy={busy}
            onDone={added}
            onProblem={setProblem}
            {...(variantOf ? { onCancel: () => setVariantOf(null) } : {})}
          />
        </div>
      )}

      {/* Hundreds at a time from a spreadsheet, on Shop Partner Pro (ruling 96). */}
      {data.catalogue && data.plan.limits?.bulkAdd && !service && (
        <BulkAdd
          catalogue={data.catalogue}
          onNews={(words) => {
            setProblem('');
            setNews(words);
            void ozi.say(words);
          }}
          onProblem={setProblem}
          onAdded={() => void load()}
        />
      )}

      <section aria-labelledby="products-heading" className="space-y-3">
        <h2 id="products-heading" className="text-lead font-bold m-0">
          {service ? 'Your services' : 'Your products'}
        </h2>
        {data.products.length === 0 && <p className="m-0">None yet.</p>}
        {data.plan.limits?.featured && data.products.length > 1 && (
          <p className="m-0">
            Shoppers see your featured products first, then the rest in this order. Use Move up and
            Move down to change it.
          </p>
        )}
        <ul className="list-none m-0 p-0 space-y-3">
          {data.products.map((product, index) =>
            data.catalogue && data.plan.limits ? (
              <ProductCard
                key={product.id}
                product={product}
                kind={data.shop.kind}
                catalogue={data.catalogue}
                limits={data.plan.limits}
                canMoveUp={index > 0}
                canMoveDown={index < data.products.length - 1}
                onNews={(words) => {
                  setProblem('');
                  setNews(words);
                }}
                onProblem={setProblem}
                onChanged={() => void load()}
                onMove={(direction) => move(product.id, direction)}
                onAddVariant={() => {
                  setVariantOf(product);
                  // Not every browser (nor the tests) can scroll; the form is there either way.
                  document.getElementById('add-product')?.scrollIntoView?.({ behavior: 'smooth' });
                }}
              />
            ) : (
              <li key={product.id} className="border-2 border-paper/40 rounded-2xl p-4 space-y-2">
                <h3 className="m-0 font-bold">
                  {product.name}, {money(product.pricePence)}
                </h3>
                <p className="m-0">
                  {STATUS_WORDS[product.status][0]?.toUpperCase()}
                  {STATUS_WORDS[product.status].slice(1)}.
                  {product.expiresOn ? ` Best before ${longDate(product.expiresOn)}.` : ''}
                  {product.note ? ` ${product.note}` : ''}
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      void removePartnerProduct(product.id).then((result) => {
                        setNews(result.message);
                        void load();
                      })
                    }
                    className="control bg-paper/10 text-paper underline"
                  >
                    Take it off<span className="visually-hidden">: {product.name}</span>
                  </button>
                </div>
              </li>
            ),
          )}
        </ul>
      </section>
      <button
        type="button"
        onClick={() => {
          rememberBusinessToken(null);
          navigate('/business');
        }}
        className="control bg-paper/10 text-paper underline"
      >
        Sign out
      </button>
    </div>
  );
}
